import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Info, CheckCircle } from 'lucide-react';

const ImageGuide = () => {
  const { i18n } = useTranslation();
  const isEL = i18n.language === 'el';

  const sections = [
    {
      title: isEL ? 'Αρχική Σελίδα' : 'Home Page',
      items: [
        {
          name: isEL ? 'Hero Image (Κεντρικό Banner)' : 'Hero Image (Main Banner)',
          dimensions: '1920 x 800 px',
          format: 'JPG/PNG',
          notes: isEL 
            ? 'Χρησιμοποιείται ως φόντο στην αρχική σελίδα. Προτιμήστε σκούρες εικόνες για καλή αναγνωσιμότητα του κειμένου.'
            : 'Used as background on the home page. Prefer dark images for good text readability.'
        }
      ]
    },
    {
      title: isEL ? 'Κατηγορίες' : 'Categories',
      items: [
        {
          name: isEL ? 'Card Image (Κάθετη Κάρτα)' : 'Card Image (Vertical Card)',
          dimensions: '600 x 800 px',
          format: 'JPG/PNG/WebP',
          notes: isEL
            ? 'Εμφανίζεται στις κάρτες κατηγοριών. Αναλογία 3:4. Καλό να έχει χώρο κάτω για τον τίτλο.'
            : 'Displayed on category cards. 3:4 aspect ratio. Good to have space at bottom for title.'
        },
        {
          name: isEL ? 'Banner Image (Οριζόντιο Banner)' : 'Banner Image (Horizontal Banner)',
          dimensions: '1920 x 600 px',
          format: 'JPG/PNG/WebP',
          notes: isEL
            ? 'Εμφανίζεται στην κορυφή της σελίδας κατηγορίας. Σκούρο φόντο για καλύτερη αναγνωσιμότητα.'
            : 'Displayed at the top of category page. Dark background for better readability.'
        }
      ]
    },
    {
      title: isEL ? 'Βίντεο' : 'Videos',
      items: [
        {
          name: isEL ? 'Thumbnail (Μικρογραφία)' : 'Video Thumbnail',
          dimensions: '1280 x 720 px',
          format: 'JPG/PNG/WebP',
          notes: isEL
            ? 'Αναλογία 16:9. Αυτή η εικόνα εμφανίζεται στη λίστα βίντεο. Τα thumbnails δημιουργούνται αυτόματα κατά το transcoding.'
            : '16:9 aspect ratio. This image appears in the video list. Thumbnails are auto-generated during transcoding.'
        }
      ]
    },
    {
      title: isEL ? 'Σύνδεση / Εγγραφή' : 'Login / Register',
      items: [
        {
          name: isEL ? 'Background Image (Φόντο)' : 'Background Image',
          dimensions: '1920 x 1080 px',
          format: 'JPG/PNG',
          notes: isEL
            ? 'Εμφανίζεται ως φόντο στις σελίδες σύνδεσης/εγγραφής. Σκούρο για καλή αντίθεση με τη φόρμα.'
            : 'Displayed as background on login/register pages. Dark for good contrast with the form.'
        }
      ]
    }
  ];

  const tips = isEL ? [
    'Χρησιμοποιήστε εργαλεία όπως Canva ή Photoshop για να φτιάξετε εικόνες στις σωστές διαστάσεις',
    'Βελτιστοποιήστε τις εικόνες πριν τις ανεβάσετε (TinyPNG, Squoosh)',
    'Για καλύτερη φόρτωση, κρατήστε τα αρχεία κάτω από 500KB',
    'WebP προσφέρει καλύτερη συμπίεση από JPG/PNG',
    'Αποφύγετε κείμενο μέσα στις εικόνες - χρησιμοποιήστε τα πεδία τίτλου/περιγραφής'
  ] : [
    'Use tools like Canva or Photoshop to create images in the correct dimensions',
    'Optimize images before uploading (TinyPNG, Squoosh)',
    'For better loading, keep files under 500KB',
    'WebP offers better compression than JPG/PNG',
    'Avoid text in images - use the title/description fields instead'
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 mb-8">
        <Image className="text-primary" size={32} />
        <div>
          <h1 className="font-roboto font-bold text-2xl text-white">
            {isEL ? 'Οδηγός Διαστάσεων Εικόνων' : 'Image Dimensions Guide'}
          </h1>
          <p className="text-gray-400 text-sm">
            {isEL ? 'Συνιστώμενες διαστάσεις για βέλτιστη εμφάνιση' : 'Recommended dimensions for optimal display'}
          </p>
        </div>
      </div>

      {/* Image Sections */}
      {sections.map((section, idx) => (
        <div key={idx} className="bg-misel-card border border-white/10 rounded-sm p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            {section.title}
          </h2>
          <div className="space-y-4">
            {section.items.map((item, itemIdx) => (
              <div key={itemIdx} className="bg-white/5 rounded-md p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-white font-medium">{item.name}</h3>
                    <p className="text-gray-400 text-sm mt-1">{item.notes}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/20 px-4 py-2 rounded-md">
                      <span className="text-primary font-mono font-bold">{item.dimensions}</span>
                    </div>
                    <div className="bg-white/10 px-3 py-2 rounded-md">
                      <span className="text-gray-300 text-sm">{item.format}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Tips Section */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-6">
        <h2 className="text-lg font-semibold text-blue-400 mb-4 flex items-center gap-2">
          <Info size={20} />
          {isEL ? 'Συμβουλές' : 'Tips'}
        </h2>
        <ul className="space-y-3">
          {tips.map((tip, idx) => (
            <li key={idx} className="flex items-start gap-3 text-gray-300">
              <CheckCircle size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Quick Reference Table */}
      <div className="bg-misel-card border border-white/10 rounded-sm overflow-hidden">
        <div className="p-4 bg-white/5 border-b border-white/10">
          <h2 className="text-white font-semibold">
            {isEL ? 'Γρήγορη Αναφορά' : 'Quick Reference'}
          </h2>
        </div>
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">
                {isEL ? 'Τύπος Εικόνας' : 'Image Type'}
              </th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">
                {isEL ? 'Διαστάσεις' : 'Dimensions'}
              </th>
              <th className="text-left p-4 text-gray-400 text-sm font-medium">
                {isEL ? 'Αναλογία' : 'Ratio'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            <tr>
              <td className="p-4 text-white">Hero Banner</td>
              <td className="p-4 text-primary font-mono">1920 x 800</td>
              <td className="p-4 text-gray-400">12:5</td>
            </tr>
            <tr>
              <td className="p-4 text-white">{isEL ? 'Κάρτα Κατηγορίας' : 'Category Card'}</td>
              <td className="p-4 text-primary font-mono">600 x 800</td>
              <td className="p-4 text-gray-400">3:4</td>
            </tr>
            <tr>
              <td className="p-4 text-white">{isEL ? 'Banner Κατηγορίας' : 'Category Banner'}</td>
              <td className="p-4 text-primary font-mono">1920 x 600</td>
              <td className="p-4 text-gray-400">16:5</td>
            </tr>
            <tr>
              <td className="p-4 text-white">Video Thumbnail</td>
              <td className="p-4 text-primary font-mono">1280 x 720</td>
              <td className="p-4 text-gray-400">16:9</td>
            </tr>
            <tr>
              <td className="p-4 text-white">{isEL ? 'Φόντο Σύνδεσης' : 'Login Background'}</td>
              <td className="p-4 text-primary font-mono">1920 x 1080</td>
              <td className="p-4 text-gray-400">16:9</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImageGuide;
